import React from 'react';
import { Callout, DocHeading, DocTable, Shot } from './components';

function PageNames() {
  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Visuals</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">Names</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Hide the player names floating over characters, and show a text of your choice instead of
        your own character name in the inventory, on the character sheet and in kill messages.
        Handy for screenshots, videos and streams. Everything is on{' '}
        <strong className="text-white">VISUALS &rsaquo; Names</strong>.
      </p>

      <Callout type="info">
        All of this changes only what <strong>your</strong> game shows. Other players still see
        your real name, and nothing is sent to the server.
      </Callout>

      <Shot
        name="names-01-names-tab.png"
        tab="VISUALS › Names"
        caption="The Names tab: the overhead name switches and your replacement name on the left, the live status on the right."
        shoot="A VISUALS › Names fül játékban. Bal oszlop: OVERHEAD NAMES kártya (legalább a Hide my own name bekapcsolva), MY OWN NAME kártya bekapcsolt „Replace my name” kapcsolóval és beírt csere-szöveggel (pl. MortisEngine), alatta a NOT AFFECTED kártya. Jobb oszlop: STATUS a „connected”, „Name field: found” és „Shown as” sorokkal. FIGYELEM: a STATUS „Real name” sora a valódi karakternevet mutatja — azt takard ki, vagy olyan karakterrel lődd, aminek a neve nyilvános lehet."
      />

      <DocHeading>Overhead Names</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        On the <strong className="text-white">OVERHEAD NAMES</strong> card. Both switches change the
        game&rsquo;s own display settings, so they behave exactly as if the game had been set that
        way &mdash; nothing is drawn over the screen.
      </p>
      <DocTable
        headers={['Switch', 'What it does']}
        rows={[
          [<strong className="text-white">Hide all player names</strong>, 'No player names float over characters — yours included.'],
          [<strong className="text-white">Hide my own name</strong>, 'Only the name over your own character is hidden.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        With the first switch on, the second one has nothing left to add; the card says so:{' '}
        <em>The first switch already covers every overhead name, including yours.</em> When you
        switch them off, the names go back to how the game had them before the bot changed them
        &mdash; so if you had your own name hidden in the game already, it stays hidden.
      </p>

      <DocHeading>My Own Name</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        On the <strong className="text-white">MY OWN NAME</strong> card. Turn on{' '}
        <strong className="text-white">Replace my name</strong> and type what should appear instead
        into the text field below it (it starts as <em>MortisEngine</em>).
      </p>
      <DocTable
        headers={['Where it changes', 'Notes']}
        rows={[
          [<strong className="text-white">Inventory headline</strong>, 'The game writes this title only once, so the bot refreshes it for you. Until that has happened, STATUS shows “Inventory headline: waiting”.'],
          [<strong className="text-white">Character sheet</strong>, 'Shows the replacement text whenever it updates.'],
          [<strong className="text-white">Kill messages</strong>, 'The message when a boss is defeated uses the replacement text.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        Other players&rsquo; names are never touched: their names come from their own characters,
        not from yours. After a relog or a map change the replacement is put back automatically
        &mdash; you do not need to switch it off and on again. When the bot unloads, your real name
        is restored on the character sheet and in kill messages; the inventory headline keeps the
        replacement until you next log in.
      </p>
      <DocTable
        headers={['If the card says', 'What it means']}
        rows={[
          [<em>Empty - nothing is written.</em>, 'The text field is empty. Type something; an empty name is never written.'],
          [<em>Too long (&hellip;), the field holds &hellip;</em>, 'The text does not fit. Nothing is written until you shorten it to the number shown. Keep the text to 47 characters or fewer: anything longer is never written, even when this line does not show.'],
          [<><em>Name field not found.</em> / <em>Nothing is written while this shows.</em></>, 'The bot cannot find your name right now — normal on the login screen and while loading. If it stays like this in the world, the game was probably updated.'],
        ]}
      />

      <DocHeading>What Is Not Covered</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The <strong className="text-white">NOT AFFECTED</strong> card lists what these options
        cannot reach: <strong className="text-white">chat lines</strong>. Names in chat come from
        somewhere else, so hiding or replacing names does not change them. Keep chat out of the
        frame if your name must not show.
      </p>
      <Callout type="warning">
        <strong>The bot&rsquo;s own menu shows your name.</strong> On the STATUS card,{' '}
        <strong>Shown as</strong> is your real name while the replacement is off, and{' '}
        <strong>Real name</strong> shows it while the replacement is on. Close the menu, or keep it
        off the recording.
      </Callout>

      <DocHeading>Status</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<strong className="text-white">Game settings</strong>, '“connected”, or “not reachable” when the bot cannot reach the game’s display settings. The OVERHEAD NAMES card then says “Waiting for the game settings.”'],
          [<><strong className="text-white">Player names</strong> · <strong className="text-white">Own name</strong></>, '“visible” or “hidden” — what the game is doing right now.'],
          [<strong className="text-white">Name field</strong>, '“found” or “not found” — whether your name can be replaced right now.'],
          [<strong className="text-white">Shown as</strong>, 'The name your game currently shows for you.'],
          [<strong className="text-white">Real name</strong>, 'Your actual character name, shown while it is replaced.'],
          [<strong className="text-white">Inventory headline</strong>, '“waiting” until the inventory title has been refreshed.'],
          [<strong className="text-white">Writes</strong>, 'How many changes have been passed to the game.'],
          [<><strong className="text-white">Delivery</strong>: queue full - not sent</>, 'A change could not be passed to the game, typically on a loading screen. It is tried again automatically.'],
        ]}
      />

      <Callout type="danger">
        <strong>After a crash, overhead names can stay hidden.</strong> The game saves these two
        display settings in its own settings file, and the bot can only put them back when it
        unloads normally &mdash; the STATUS card warns about this while names are hidden. The
        switches cannot undo it afterwards, because the bot restores what it found when it
        started, and by then the game had already saved the hidden state. The fix is on the game
        side: its settings file has to be reset (the game creates a new one). Contact support if
        you need help with that.
      </Callout>
    </div>
  );
}

export default PageNames;
